/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { Area } from "@antv/g2plot";
import { getRange } from "../mitigation-actions-trend/helper";

const colors = [
  '#74BDCB',
  '#3D550C',
  '#81B622',
  '#FFA384',
  '#EFE7BC',
  '#145DA0',
  '#ECF87F',
  '#0C2D48',
  '#2E8BC0',
  '#B1D4E0',
  '#59981A',
];

export function useChartHelper({ data, t = (s) => s }) {
  const container = useRef();
  const [graph, setGraph] = useState(null);

  const mappedData = useMemo(() => {
    if (data && data.graphSources) {
      let mData = [];
      const values = {};

      const n = 2;
      const first = Object.values(data.graphSources)[0];
      let graphSources = data.graphSources;

      if (Object.keys(first).length > n) {
        const otherKeys = Object.keys(first).slice(n);
        graphSources = Object.keys(graphSources).reduce((all, key) => {
          const _new = {
            ...Object.keys(data.graphSources[key]).reduce((all, k) => {
              if (!otherKeys.includes(k)) {
                all[k] = data.graphSources[key][k];
              }
              return all;
            }, {}),
            Other: Object.keys(data.graphSources[key]).reduce((all, k) => {
              if (otherKeys.includes(k)) {
                return all + (data.graphSources[key][k] || 0)
              }
              return all;
            }, 0),
          };
          all[key] = _new;
          return all;
        }, {});
      }

      Object.entries(graphSources).forEach(([k, v]) => {
        Object.entries(v).forEach(([K, V]) => {
          if (values[K]) {
            values[K] += V;
          } else {
            values[K] = V;
          }
          mData.push({
            type: k,
            value: values[K],
            series: K,
            realValue: V,
          });
        });
      });

      if (!mData.length) {
        Object.entries(graphSources).forEach(([k]) => {
          mData.push({
            type: k,
            series: "",
          });
        });
      }
      return mData;
    }
  }, [data]);

  const sumData = useMemo(() => {
    const _values = {};

    mappedData.forEach((val) => {
      if (typeof _values[val.series] === 'number') {
        _values[val.series] += val.realValue;
      } else {
        _values[val.series] = val.realValue;
      }
    })

    return _values;
  }, [mappedData]);

  const tooltip = {
    customContent: (label, data) => {
      if (Array.isArray(data) && label) {
        const sources = [];

        data.forEach((d, i) => {
          if (d && d.data && typeof d.data.realValue === 'number') {
            const _total = sumData[d.name];

            sources.push({
              color: colors[i],
              value: d.data.realValue,
              total: _total,
              name: d.name,
            });
          }
        });

        const getSum = (data, key = 'value') => {
          let sum = 0;
          data.forEach((d) => {
            sum += d[key];
          })
          return sum;
        }

        const n = 2;

        return `
          <div class="custom-tooltip">
            <div class="section" style="margin-bottom: 12px;">
              <h4>
                ${t('Recommendations')}
              </h4>
            </div>
            <div class="section" style="margin-bottom: 0px;">
              <div class="label">
                <h4></h4>
              </div>
              <div class="value week-val">
                <label class="value">
                  ${label.replace('W', `${t('Week')} `)}
                </label>
              </div>
              <div class="value total-val">
                <label class="value">
                  ${t('Total')}
                </label>
              </div>
            </div>
            ${sources.length > n ? (`
            ${sources.slice(0, n).map((s) => `
              <div class="section">
                <div class="label sources">
                  <div class="legend" style="background: ${s.color}"></div>
                  <h4>${s.name}</h4>
                </div>
                <div class="value sources week-val">
                  <label class="value">${s.value}</label>
                </div>
                <div class="value sources total-val">
                  <label class="value">${s.total}</label>
                </div>
              </div>
              `).join('')
            }
            <div class="section">
                <div class="label sources">
                  <div class="legend" style="background: ${colors[n]}"></div>
                  <h4>${t('Other')}</h4>
                </div>
                <div class="value sources week-val">
                  <label class="value">
                    ${getSum(sources.slice(n))}
                  </label>
                </div>
                <div class="value sources total-val">
                  <label class="value">
                    ${getSum(sources.slice(n), 'total')}
                  </label>
                </div>
              </div>
            `) : (`
            ${sources.map((s) => `
              <div class="section">
                <div class="label sources">
                  <div class="legend" style="background: ${s.color}"></div>
                  <h4>${s.name}</h4>
                </div>
                <div class="value sources week-val">
                  <label class="value">${s.value}</label>
                </div>
                <div class="value sources total-val">
                  <label class="value">${s.total}</label>
                </div>
              </div>
              `).join('')
            }`
          )}
          </div>
        `;
      }
      return '';
    },
  }

  const createGraph = () => {
    const chart = new Area(container.current, {
      data: mappedData,
      xField: "type",
      yField: "value",
      seriesField: "series",
      legend: false,
      xAxis: {
        label: {
          style: {
            fontSize: 10
          },
        },
      },
      yAxis: {
        label: {
          formatter: (a) => Number(a).toLocaleString('en-US'),
        },
        tickMethod: (a) => getRange(a.max),
      },
      tooltip,
      areaStyle: {
        "fillOpacity": 0.2
      },
      color: colors,
    });

    chart.render();
    setGraph(chart);
  };

  useEffect(() => {
    if (mappedData) {
      if (!graph) {
        createGraph();
      } else {
        graph.update({
          data: mappedData,
          tooltip,
        });
      }
    }
  }, [mappedData]);

  return {
    container,
  };
};
